import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { productsByCategory, categories, heroImages } from "../../data/productData";

const NewCategoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const categoryData = productsByCategory[id];
  const categoryMeta = categories.find((c) => c.link === id) || {};
  const [activeTab, setActiveTab] = useState("");
  const [activeOption, setActiveOption] = useState("");
  const [activeNested, setActiveNested] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!categoryData) return;
    const first = Object.keys(categoryData)[0];
    setActiveTab(first || "");
  }, [id, categoryData]);

  if (!categoryData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-2">Category not found</h2>
          <p className="text-gray-600">We couldn't find the category you requested.</p>
        </div>
      </div>
    );
  }

  const resolveOptions = () => {
    const entry = categoryData[activeTab];
    if (!entry) return null;
    if (entry.options) return entry.options;
    if (entry.products && Array.isArray(entry.products)) return null;
    // fallback: entry might be an array
    if (Array.isArray(entry)) return null;
    return null;
  };

  const getProductsForView = () => {
    const entry = categoryData[activeTab];
    if (!entry) return [];
    // array
    if (Array.isArray(entry)) return entry;
    if (entry.products) return entry.products;
    if (entry.options) {
      if (!activeOption) return [];
      const opt = entry.options[activeOption];
      if (!opt) return [];
      if (opt.products) return opt.products;
      if (opt.options) {
        if (!activeNested) return [];
        return opt.options[activeNested]?.products || [];
      }
    }
    return [];
  };

  const products = getProductsForView().filter((p) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (p.name || "").toLowerCase().includes(s) ||
      (p.use || "").toLowerCase().includes(s) ||
      (p.description || "").toLowerCase().includes(s)
    );
  });

  const handleProductClick = (product) => {
    const pid = product.id;
    navigate(`/new-products/${id}/${pid}`);
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: "linear-gradient(180deg, var(--accent-50, #e8f9f8), #ffffff 40%, var(--comp-50, #fbf7ff))",
      }}>
      {/* Hero full-bleed (category) */}
      <div className="relative mb-8 w-full overflow-hidden">
        <div
          className="w-full h-64 md:h-[520px] bg-center bg-cover"
          style={{ backgroundImage: `url(${categoryMeta.image || heroImages[1]})` }}
        />
        <div className="absolute inset-0 bg-black/40 flex items-center">
          <div className="max-w-7xl mx-auto px-4 py-8 lg:px-12 text-left text-white">
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">{categoryMeta.name || id}</h1>
            <p className="mt-3 max-w-2xl text-sm md:text-lg font-light">
              {categoryMeta.desc || categoryData.description}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumbs (top) */}
        <div className="mb-6">
          <nav
            className="breadcrumbs"
            aria-label="Breadcrumb">
            <ol className="flex items-center">
              <li>
                <Link
                  to="/"
                  className="hover:underline">
                  Home
                </Link>
              </li>
              <li aria-hidden>
                <svg
                  className="w-4 h-4 text-gray-400 mx-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </li>
              <li>
                <Link
                  to="/our-products"
                  className="hover:underline">
                  Products
                </Link>
              </li>
              <li aria-hidden>
                <svg
                  className="w-4 h-4 text-gray-400 mx-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </li>
              <li className="crumb-current">{categoryMeta.name || id}</li>
            </ol>
          </nav>
        </div>

        {/* hero relocated to full-bleed above */}

        <div className="mb-6">
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full md:w-1/2 px-4 py-2 border rounded-lg"
          />
        </div>

        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {Object.keys(categoryData).map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setActiveOption("");
                  setActiveNested("");
                }}
                className={`px-3 py-2 rounded-full ${activeTab === tab ? "bg-accent text-white" : "bg-gray-100"}`}>
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Options */}
        {(() => {
          const options = resolveOptions();
          if (!options) return null;
          return (
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-2">Options</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {Object.keys(options).map((ok) => (
                  <button
                    key={ok}
                    onClick={() => {
                      setActiveOption(ok);
                      setActiveNested("");
                    }}
                    className={`px-3 py-1 rounded-full ${
                      activeOption === ok ? "bg-accent text-white" : "bg-gray-100"
                    }`}>
                    {ok}
                  </button>
                ))}
              </div>

              {/* nested options if present */}
              {activeOption && options[activeOption] && options[activeOption].options && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(options[activeOption].options).map((n) => (
                      <button
                        key={n}
                        onClick={() => setActiveNested(n)}
                        className={`px-3 py-1 rounded-full ${
                          activeNested === n ? "bg-accent text-white" : "bg-gray-100"
                        }`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Products grid */}
       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 px-2 md:px-0">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-xl shadow p-4 cursor-pointer"
                onClick={() => handleProductClick(product)}
              >

              <div className="h-44 overflow-hidden rounded mb-3">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <h4 className="font-semibold mb-1">{product.name}</h4>
              <p className="text-sm text-gray-600 mb-2">{product.use}</p>
              <p className="text-sm text-gray-700 line-clamp-3 mb-3">{product.description}</p>
              <div className="flex items-center justify-between">
                <span
                  className="text-sm font-medium"
                  style={{ color: "var(--accent)" }}>
                  {product.price}
                </span>
                <Link to="/contact-us">
                  <button className="btn-accent text-white px-3 py-1 rounded">Get Quote</button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewCategoryPage;
